import { useState, useEffect } from 'react';
import { getOrderStatisticsWithComparison } from '../services/orderService';
import { MonthlySnapshot } from '../types';

interface UseOrderStatisticsResult {
  statistics: {
    paidSucceededOrders: number;
    shippedOrders: number;
    totalOrders: number;
    totalRevenue: number;
  } | null;
  previousSnapshot: MonthlySnapshot | null;
  percentageChanges: {
    paidSucceededOrders: number;
    shippedOrders: number;
    totalOrders: number;
    totalRevenue: number;
  } | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useOrderStatistics = (): UseOrderStatisticsResult => {
  const [statistics, setStatistics] = useState<{
    paidSucceededOrders: number;
    shippedOrders: number;
    totalOrders: number;
    totalRevenue: number;
  } | null>(null);
  const [previousSnapshot, setPreviousSnapshot] = useState<MonthlySnapshot | null>(null);
  const [percentageChanges, setPercentageChanges] = useState<{
    paidSucceededOrders: number;
    shippedOrders: number;
    totalOrders: number;
    totalRevenue: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getOrderStatisticsWithComparison();
      setStatistics(result.current);
      setPreviousSnapshot(result.previous);
      setPercentageChanges(result.percentageChanges);
    } catch (err) {
      console.error('Error fetching order statistics:', err);
      setError('Failed to load order statistics');
      setStatistics(null);
      setPreviousSnapshot(null);
      setPercentageChanges(null);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    await fetchStatistics();
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  return {
    statistics,
    previousSnapshot,
    percentageChanges,
    loading,
    error,
    refresh
  };
}; 